import React from "react";
import { Route, Switch, useHistory, Redirect } from "react-router-dom";

import * as navigationCustomEvent from "./components/sidebar/utils/navigationCustomEvent";
import { NotFound } from "./components/system";
import RemoteHostComponent, { RemoteHostComponentTypes } from "./components/system/remoteHostComponent";
import configuration from "./configuration";
import { claimPageUrl } from "./constants";
import { useBuyNowJourney } from "./hooks/useBuyNowJourney";
import { remotes, redirects } from "./remotes";
import { BaseDataResponseBody, ShellDataResponseBody } from "./types";
import { useAnalytics } from "./utils/analytics";
import { changeBusinessUnit } from "./utils/changeAccount";
import { initializeSentry } from "./utils/helpers";
import { isPaidDomain } from "./utils/isPaidDomain";

interface RoutesProps extends BaseDataResponseBody {
  localeLocalizationProviderOverride: string;
  componentOverride: React.ReactElement;
  signupContext?: ShellDataResponseBody["signupContext"];
}

const stylesReset = (history) => {
  history.listen(({ pathname }) => {
    const activeMicrosite = remotes.find(({ url }) => url === pathname.split("/")[1]);

    Array.prototype.slice.call(document.querySelectorAll("[data-microsites-styles]")).forEach((t) => {
      const dataAttribute = t.dataset.micrositesStyles;

      t.disabled =
        (!activeMicrosite && dataAttribute !== "shell") ||
        (dataAttribute !== "shell" && dataAttribute !== activeMicrosite.scope);
    });
  });
};

const Routes = React.memo(
  ({
    locale,
    localeLocalizationProviderOverride,
    businessUser,
    businessUnit,
    launchDarkly,
    componentOverride,
    signupContext,
  }: RoutesProps) => {
    const hasBusinessUnit = Boolean(businessUnit);
    const history = useHistory();
    const analytics = useAnalytics();
    useBuyNowJourney(businessUser.id, signupContext, hasBusinessUnit);

    const customProps: RemoteHostComponentTypes["customProps"] = {
      locale,
      localeLocalizationProviderOverride,
      businessUser,
      businessUnit,
      launchDarkly,
      analytics,
      shellConnect: {
        history,
        navigate: (path: string) => history.push(path),
        initializeSentry: (config) => initializeSentry(businessUser.id, config),
        changeBusinessUnit,
        navigationCustomEvent,
      },
    };

    // On routing disable styles that are added by the microsites.
    // This is done to prevent styles from one microsite stylesheet affecting other microsites.
    stylesReset(history);

    if (componentOverride) {
      return <div>{React.cloneElement(componentOverride, customProps)}</div>;
    }

    /* Users without business units are redirected to the claim domain page */
    if (!hasBusinessUnit) {
      const { url, path, scope, module } = remotes.find(({ url }) => url === "onboarding");

      return (
        <Switch>
          <Route path={`/${url}`}>
            <RemoteHostComponent
              config={{
                path,
                scope,
                module,
              }}
              customProps={customProps}
            />
          </Route>
          <Redirect
            to={{
              pathname: claimPageUrl,
              search: history.location.search,
            }}
          />
        </Switch>
      );
    }

    return (
      <Switch>
        {Object.entries(redirects).map(([from, to]) => (
          <Redirect
            key={from}
            from={from}
            to={{
              pathname: to,
              search: history.location.search,
            }}
          />
        ))}

        {remotes.map(({ scope, url, path, module }) => (
          <Route path={`/${url}`} key={url}>
            <RemoteHostComponent
              config={{
                path,
                scope,
                module,
              }}
              customProps={customProps}
            />
          </Route>
        ))}
        {/* Free users go by default to the onboarding app */}
        <Route
          path="/"
          exact
          render={() => {
            if (configuration.Environment === "Development") {
              return null;
            }

            return <Redirect to={isPaidDomain(businessUnit?.availableFeatures) ? "/dashboard" : "/onboarding"} />;
          }}
        />
        <Route path="*" component={NotFound} />
      </Switch>
    );
  },
);

export default Routes;
